import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, where, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import * as XLSX from 'xlsx';

function SearchDropdown({ label, value, options, onSelect, placeholder = 'Seçiniz veya arayın...' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ marginBottom: 12 }}>
      <label className="label">{label}</label>
      <div className="dropdown-btn" onClick={() => { setSearch(''); setOpen(true); }}>
        <span style={{ color: value ? '#1a1a2e' : '#aaa' }}>{value || placeholder}</span>
        <span>▼</span>
      </div>
      {open && (
        <div className="dropdown-overlay" onClick={() => setOpen(false)}>
          <div className="dropdown-modal" onClick={e => e.stopPropagation()}>
            <div className="dropdown-header">
              <span className="dropdown-title">{label}</span>
              <span className="dropdown-close" onClick={() => setOpen(false)}>✕</span>
            </div>
            <input className="input" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)} autoFocus style={{ margin: '8px 12px', width: 'calc(100% - 24px)' }} />
            <div className="dropdown-list">
              {filtered.length === 0 ? <div className="dropdown-empty">Sonuç bulunamadı</div> : filtered.map(opt => (
                <div key={opt} className={`dropdown-item ${value === opt ? 'selected' : ''}`} onClick={() => { onSelect(opt); setOpen(false); }}>
                  {opt}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const emptyForm = () => ({ bayi: '', marka: '', urun: '', durum: '', projeAdi: '', adet: '', teslimatTarihi: '', notlar: '', urunler: [] });

export default function OrderForm({ currentUser }) {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [bayiler, setBayiler] = useState([]);
  const [markalar, setMarkalar] = useState({});
  const [markaKasa, setMarkaKasa] = useState({});
  const [durumlar, setDurumlar] = useState([]);
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [copyFromPeriodId, setCopyFromPeriodId] = useState('');
  const [copyFromOrders, setCopyFromOrders] = useState([]);
  const [copySearch, setCopySearch] = useState('');
  const [selectedToCopy, setSelectedToCopy] = useState([]);
  const [copyAdetler, setCopyAdetler] = useState({});
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => { loadPeriods(); loadLists(); loadDurumlar(); }, []);

  const loadPeriods = async () => {
    const q = query(collection(db, 'periods'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    setPeriods(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const loadDurumlar = async () => {
    const snap = await getDocs(collection(db, 'durumlar'));
    const list = snap.docs.map(d => d.data().ad);
    list.sort((a, b) => a.localeCompare(b, 'tr'));
    setDurumlar(list.length > 0 ? list : ['Teklif', 'Kesinleşti', 'İptal']);
  };

  const loadLists = async () => {
    const bayiSnap = await getDocs(collection(db, 'bayiler'));
    const bayiList = bayiSnap.docs.map(d => d.data().ad).sort((a, b) => a.localeCompare(b, 'tr'));
    setBayiler(bayiList);
    const markaSnap = await getDocs(collection(db, 'markalar'));
    const markaData = markaSnap.docs.map(d => d.data());
    const grouped = {};
    const kasaMap = {};
    markaData.forEach(m => {
      if (!grouped[m.marka]) grouped[m.marka] = [];
      grouped[m.marka].push(m.urun);
      kasaMap[`${m.marka}__${m.urun}`] = m.kasa || false;
    });
    Object.keys(grouped).forEach(k => grouped[k].sort((a, b) => a.localeCompare(b, 'tr')));
    setMarkalar(grouped);
    setMarkaKasa(kasaMap);
  };

  const loadOrders = async (periodId) => {
    const q = query(collection(db, 'orders'), where('periodId', '==', periodId), where('userId', '==', auth.currentUser.uid));
    const snap = await getDocs(q);
    setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const loadArchivedOrders = async (periodId) => {
    const q = query(collection(db, 'archivedOrders'), where('periodId', '==', periodId), where('userId', '==', auth.currentUser.uid));
    const snap = await getDocs(q);
    setArchivedOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const loadCopyFromOrders = async (periodId) => {
    const q = query(collection(db, 'orders'), where('periodId', '==', periodId), where('userId', '==', auth.currentUser.uid));
    const snap = await getDocs(q);
    const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setCopyFromOrders(loaded);
    const adetler = {};
    loaded.forEach(o => {
      o.urunler?.forEach((u, i) => { adetler[`${o.id}_${i}`] = u.adet; });
    });
    setCopyAdetler(adetler);
  };

  const selectPeriod = (p, readOnly = false) => {
    setSelectedPeriod(p);
    setViewOnly(readOnly);
    loadOrders(p.id);
    loadArchivedOrders(p.id);
  };

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const kasaGerektiriyor = (marka, urun) => markaKasa[`${marka}__${urun}`] || false;
  const removeUrun = (idx) => setForm(f => ({ ...f, urunler: f.urunler.filter((_, i) => i !== idx) }));

  const openEdit = (o) => {
    setEditId(o.id);
    setForm({ projeAdi: o.projeAdi || '', durum: o.projeDurumu || '', bayi: o.bayiAdi || '', marka: '', urun: '', adet: '', teslimatTarihi: o.teslimatTarihi || '', notlar: o.notlar || '', urunler: o.urunler || [] });
    setShowForm(true);
  };

  const deleteOrder = async (id) => { setDeleteConfirmId(id); };

  const confirmDelete = async () => {
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    const orderSnap = orders.find(o => o.id === id);
    if (orderSnap) {
      const { id: _, ...orderData } = orderSnap;
      await addDoc(collection(db, 'archivedOrders'), { ...orderData, deletedAt: new Date() });
    }
    await deleteDoc(doc(db, 'orders', id));
    loadOrders(selectedPeriod.id);
    loadArchivedOrders(selectedPeriod.id);
  };

  const restoreOrder = async (o) => {
    const { id, deletedAt, ...orderData } = o;
    await addDoc(collection(db, 'orders'), orderData);
    await deleteDoc(doc(db, 'archivedOrders', id));
    loadOrders(selectedPeriod.id);
    loadArchivedOrders(selectedPeriod.id);
  };

  const closeForm = () => { setForm(emptyForm()); setShowForm(false); setEditId(null); };
  const closeCopyModal = () => { setShowCopyModal(false); setCopyFromPeriodId(''); setCopyFromOrders([]); setCopySearch(''); setSelectedToCopy([]); setCopyAdetler({}); };

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!form.bayi || !form.durum || !form.projeAdi || form.urunler.length === 0) { alert('Bayi, proje adı, proje durumu ve en az 1 ürün gerekli!'); return; }
    setLoading(true);
    try {
      const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', auth.currentUser.uid)));
      const userName = userSnap.docs[0]?.data().name || auth.currentUser.email;
      if (editId) {
        await updateDoc(doc(db, 'orders', editId), { bayiAdi: form.bayi, projeDurumu: form.durum, projeAdi: form.projeAdi, urunler: form.urunler, teslimatTarihi: form.teslimatTarihi, notlar: form.notlar });
      } else {
        await addDoc(collection(db, 'orders'), {
          periodId: selectedPeriod.id, periodName: selectedPeriod.name,
          userId: auth.currentUser.uid, satisYoneticisi: userName,
          bayiAdi: form.bayi, projeDurumu: form.durum, projeAdi: form.projeAdi,
          urunler: form.urunler, teslimatTarihi: form.teslimatTarihi, notlar: form.notlar,
          createdAt: new Date(),
        });
      }
      closeForm();
      loadOrders(selectedPeriod.id);
    } catch (err) { alert('Hata: ' + err.message); }
    setLoading(false);
  };

  const exportExcel = () => {
    if (orders.length === 0) { alert('Sipariş yok!'); return; }
    const rows = [];
    orders.forEach(o => {
      if (o.urunler && o.urunler.length > 0) {
        o.urunler.forEach(u => rows.push({
          'Proje Adı': o.projeAdi || '', 'Proje Durumu': o.projeDurumu || '',
          'Bayi Adı': o.bayiAdi || '', 'Marka': u.marka || '',
          'Ürün Adı': u.urun || '', 'Adet': u.adet || '',
          'Teslim Edildi': u.teslimEdildi ? 'Evet' : 'Hayır',
          'Teslim Adedi': u.teslimAdet || '',
          'Teslimat Tarihi': o.teslimatTarihi || '', 'Notlar': o.notlar || ''
        }));
      }
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Siparişler');
    XLSX.writeFile(wb, `${selectedPeriod.name}_siparisler.xlsx`);
  };

  const downloadTemplate = () => {
    const rows = [{ 'Proje Adı': '', 'Proje Durumu': '', 'Bayi Adı': '', 'Marka': '', 'Ürün Adı': '', 'Adet': '', 'Teslimat Tarihi': '', 'Notlar': '' }];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Siparişler');
    XLSX.writeFile(wb, 'siparis_sablonu.xlsx');
  };

  const importOrders = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', auth.currentUser.uid)));
        const userName = userSnap.docs[0]?.data().name || auth.currentUser.email;
        const existingSnap = await getDocs(query(collection(db, 'orders'), where('periodId', '==', selectedPeriod.id), where('userId', '==', auth.currentUser.uid)));
        const existingKeys = new Set(existingSnap.docs.map(d => `${d.data().projeAdi}_${d.data().bayiAdi}_${d.data().projeDurumu}`));
        const grouped = {};
        for (const row of rows) {
          if (!row['Bayi Adı'] || !row['Proje Adı']) continue;
          const key = `${row['Proje Adı']}_${row['Bayi Adı']}_${row['Proje Durumu']}_${row['Teslimat Tarihi']}`;
          if (!grouped[key]) {
            grouped[key] = { bayiAdi: String(row['Bayi Adı'] || ''), projeDurumu: String(row['Proje Durumu'] || ''), projeAdi: String(row['Proje Adı'] || ''), teslimatTarihi: String(row['Teslimat Tarihi'] || ''), notlar: String(row['Notlar'] || ''), urunler: [] };
          }
          if (row['Marka'] && row['Ürün Adı']) {
            grouped[key].urunler.push({ marka: String(row['Marka'] || ''), urun: String(row['Ürün Adı'] || ''), adet: String(row['Adet'] || ''), kasa: false, teslimEdildi: false, teslimAdet: '' });
          }
        }
        let count = 0; let skipped = 0;
        for (const key of Object.keys(grouped)) {
          const o = grouped[key];
          const dupKey = `${o.projeAdi}_${o.bayiAdi}_${o.projeDurumu}`;
          if (existingKeys.has(dupKey)) { skipped++; continue; }
          await addDoc(collection(db, 'orders'), { periodId: selectedPeriod.id, periodName: selectedPeriod.name, userId: auth.currentUser.uid, satisYoneticisi: userName, bayiAdi: o.bayiAdi, projeDurumu: o.projeDurumu, projeAdi: o.projeAdi, urunler: o.urunler, teslimatTarihi: o.teslimatTarihi, notlar: o.notlar, createdAt: new Date() });
          count++;
        }
        await loadOrders(selectedPeriod.id);
        alert(`✅ ${count} sipariş eklendi!${skipped > 0 ? ` ${skipped} zaten mevcut olduğu için atlandı.` : ''}`);
      } catch (err) { alert('❌ Hata: ' + err.message); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const toggleSelectCopy = (id) => setSelectedToCopy(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const existingOrderKeys = new Set(orders.map(o => `${o.projeAdi}_${o.bayiAdi}_${o.projeDurumu}`));
  const filteredCopyOrders = copyFromOrders.filter(o =>
    o.projeAdi?.toLowerCase().includes(copySearch.toLowerCase()) ||
    o.bayiAdi?.toLowerCase().includes(copySearch.toLowerCase()) ||
    o.urunler?.some(u => u.urun?.toLowerCase().includes(copySearch.toLowerCase()))
  );

  const copySelectedOrders = async () => {
    if (selectedToCopy.length === 0) { alert('Kopyalanacak sipariş seç!'); return; }
    let count = 0; let skipped = 0;
    for (const id of selectedToCopy) {
      const o = copyFromOrders.find(o => o.id === id);
      if (!o) continue;
      const dupKey = `${o.projeAdi}_${o.bayiAdi}_${o.projeDurumu}`;
      if (existingOrderKeys.has(dupKey)) { skipped++; continue; }
      const updatedUrunler = o.urunler?.map((u, i) => ({ ...u, adet: copyAdetler[`${id}_${i}`] || u.adet, teslimEdildi: false, teslimAdet: '' }));
      const { id: _, ...orderData } = o;
      await addDoc(collection(db, 'orders'), { ...orderData, urunler: updatedUrunler, periodId: selectedPeriod.id, periodName: selectedPeriod.name, createdAt: new Date() });
      count++;
    }
    await loadOrders(selectedPeriod.id);
    alert(`✅ ${count} sipariş kopyalandı!${skipped > 0 ? ` ${skipped} zaten mevcut.` : ''}`);
    closeCopyModal();
  };

  const updateOrderUrunAdet = async (orderId, urunler, idx, val) => {
    if (!val) return;
    const newUrunler = [...urunler];
    newUrunler[idx] = { ...newUrunler[idx], adet: val };
    await updateDoc(doc(db, 'orders', orderId), { urunler: newUrunler });
    loadOrders(selectedPeriod.id);
  };

  const toggleTeslimEdildi = async (orderId, urunler, idx) => {
    const newUrunler = [...urunler];
    const current = newUrunler[idx];
    const newVal = !current.teslimEdildi;
    newUrunler[idx] = { ...current, teslimEdildi: newVal, teslimAdet: newVal ? current.adet : '' };
    await updateDoc(doc(db, 'orders', orderId), { urunler: newUrunler });
    loadOrders(selectedPeriod.id);
  };

  const updateTeslimAdet = async (orderId, urunler, idx, val) => {
    const newUrunler = [...urunler];
    newUrunler[idx] = { ...newUrunler[idx], teslimAdet: val };
    await updateDoc(doc(db, 'orders', orderId), { urunler: newUrunler });
    loadOrders(selectedPeriod.id);
  };

  if (!selectedPeriod) {
    return (
      <div>
        <div className="card">
          <h2>Dönem Seç</h2>
          {periods.filter(p => p.status === 'active').length > 0 && (
            <div>
              <div className="upload-section-title">Aktif Dönemler</div>
              {periods.filter(p => p.status === 'active').map(p => (
                <div key={p.id} className="period-btn" onClick={() => selectPeriod(p, false)}>
                  <div className="period-name">{p.name}</div>
                  <div className="period-date">{p.startDate} → {p.endDate}</div>
                </div>
              ))}
            </div>
          )}
          {periods.filter(p => p.status === 'closed').length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="upload-section-title">Geçmiş Dönemler</div>
              {periods.filter(p => p.status === 'closed').map(p => (
                <div key={p.id} className="period-btn" style={{ background: '#f0f0f0', cursor: 'pointer' }} onClick={() => selectPeriod(p, true)}>
                  <div className="period-name" style={{ color: '#999' }}>{p.name}</div>
                  <div className="period-date">{p.startDate} → {p.endDate}</div>
                  <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>Sadece görüntüleme</div>
                </div>
              ))}
            </div>
          )}
          {periods.filter(p => p.status === 'active').length === 0 && (
            <p style={{ color: '#888', textAlign: 'center', marginTop: 8 }}>Aktif dönem yok</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="period-header">
          <div>
            <div className="period-name">{selectedPeriod.name}</div>
            <div className="period-date">{selectedPeriod.startDate} → {selectedPeriod.endDate}</div>
            {viewOnly && <div style={{ fontSize: 12, color: '#e63946', marginTop: 2, fontWeight: 600 }}>👁 Sadece görüntüleme</div>}
          </div>
          <button className="btn-secondary" onClick={() => { setSelectedPeriod(null); setViewOnly(false); }}>← Geri</button>
        </div>
      </div>

      {!viewOnly && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn-primary" onClick={() => { setForm(emptyForm()); setEditId(null); setShowForm(true); }}>+ Yeni Sipariş</button>
            <button className="btn-primary" onClick={() => setShowCopyModal(true)}>+ Eski Dönemden Kopyala</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={downloadTemplate}>Şablon İndir</button>
            <label className="btn-secondary" style={{ cursor: 'pointer' }}>
              Sipariş Yükle
              <input type="file" accept=".xlsx,.xls" onChange={importOrders} style={{ display: 'none' }} />
            </label>
            {orders.length > 0 && <button className="btn-secondary" onClick={exportExcel}>Sipariş Özeti İndir</button>}
            <button className="btn-secondary" onClick={() => setShowArchive(!showArchive)}>
              {showArchive ? 'Siparişlere Dön' : `Arşiv (${archivedOrders.length})`}
            </button>
          </div>
        </>
      )}

      {viewOnly && (
        <div style={{ marginBottom: 16 }}>
          {orders.length > 0 && <button className="btn-secondary" onClick={exportExcel}>Sipariş Özeti İndir</button>}
        </div>
      )}

      {showArchive && !viewOnly ? (
        <div>
          <h3 className="section-title">Arşivlenen Siparişler</h3>
          {archivedOrders.length === 0 && <div className="card"><p style={{ color: '#888', textAlign: 'center' }}>Arşiv boş</p></div>}
          {archivedOrders.map(o => (
            <div key={o.id} className="card order-card">
              <div className="order-header">
                <div className="order-title">{o.projeAdi}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-secondary" onClick={() => restoreOrder(o)}>Geri Al</button>
                  <button className="btn-delete" onClick={async () => { await deleteDoc(doc(db, 'archivedOrders', o.id)); loadArchivedOrders(selectedPeriod.id); }}>Kalıcı Sil</button>
                </div>
              </div>
              <div className="order-detail">{o.bayiAdi} | {o.projeDurumu}</div>
              {o.urunler?.map((u, i) => <div key={i} className="order-detail">• {u.marka} — {u.urun} x{u.adet}</div>)}
            </div>
          ))}
        </div>
      ) : (
        <div>
          {orders.map(o => (
            <div key={o.id} className="card order-card">
              <div className="order-header">
                <div className="order-title">{o.projeAdi}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={`badge ${o.projeDurumu === 'Kesinleşti' ? 'badge-green' : o.projeDurumu === 'İptal' ? 'badge-red' : 'badge-yellow'}`}>{o.projeDurumu}</span>
                  {!viewOnly && (
                    <>
                      <button className="btn-secondary" onClick={() => openEdit(o)}>Düzenle</button>
                      <button className="btn-delete" onClick={() => deleteOrder(o.id)}>Sil</button>
                    </>
                  )}
                </div>
              </div>
              <div className="order-detail">{o.bayiAdi}</div>
              {o.urunler?.map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f8f8f8' }}>
                  <span className="order-detail" style={{ flex: 1 }}>• {u.marka} — {u.urun}</span>
                  {!viewOnly ? (
                    <input
                      type="number" value={u.adet} min="1"
                      onChange={e => updateOrderUrunAdet(o.id, o.urunler, i, e.target.value)}
                      style={{ width: 55, padding: '3px 6px', border: '1px solid #e0e4ff', borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: 'center' }}
                    />
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>x{u.adet}</span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={u.teslimEdildi || false}
                      onChange={() => toggleTeslimEdildi(o.id, o.urunler, i)}
                      id={`teslim_${o.id}_${i}`}
                      style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor={`teslim_${o.id}_${i}`} style={{ fontSize: 12, color: u.teslimEdildi ? '#10b981' : '#aaa', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {u.teslimEdildi ? '✓' : ''} Teslim Edildi
                    </label>
                    {u.teslimEdildi && (
                      <input
                        type="number"
                        value={u.teslimAdet || u.adet}
                        min="1" max={u.adet}
                        onChange={e => updateTeslimAdet(o.id, o.urunler, i, e.target.value)}
                        style={{ width: 45, padding: '2px 4px', border: '1px solid #10b981', borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center', color: '#10b981' }}
                      />
                    )}
                  </div>
                </div>
              ))}
              {o.teslimatTarihi && <div className="order-detail" style={{ marginTop: 4 }}>Teslimat: {o.teslimatTarihi}</div>}
              {o.notlar && <div className="order-note">{o.notlar}</div>}
            </div>
          ))}
        </div>
      )}

      {deleteConfirmId && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 360 }}>
            <div className="modal-header">
              <h2>Siparişi Sil</h2>
              <span className="dropdown-close" onClick={() => setDeleteConfirmId(null)}>✕</span>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: '#555' }}>Bu siparişi silmek istediğine emin misin? Arşive taşınacak.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-primary" style={{ flex: 1, background: '#e63946' }} onClick={confirmDelete}>Evet, Sil</button>
                <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setDeleteConfirmId(null)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editId ? 'Siparişi Düzenle' : 'Yeni Sipariş'}</h2>
              <span className="dropdown-close" onClick={closeForm}>✕</span>
            </div>
            <div className="modal-body">
              <form onSubmit={submitOrder}>
                <label className="label">Proje Adı *</label>
                <input className="input" placeholder="Proje adını gir" value={form.projeAdi} onChange={e => setF('projeAdi', e.target.value)} required />
                <SearchDropdown label="Proje Durumu *" value={form.durum} options={durumlar} onSelect={v => setF('durum', v)} />
                <SearchDropdown label="Bayi Adı *" value={form.bayi} options={bayiler} onSelect={v => setF('bayi', v)} />
                <label className="label">Beklenen Teslimat Tarihi</label>
                <input className="input" type="date" value={form.teslimatTarihi} min={new Date().toISOString().split('T')[0]} onChange={e => setF('teslimatTarihi', e.target.value)} />
                <label className="label">Notlar</label>
                <textarea className="input" placeholder="Notlar" value={form.notlar} onChange={e => setF('notlar', e.target.value)} rows={3} />
                <div className="urun-section">
                  <h3>Ürünler *</h3>
                  {form.urunler.map((u, i) => (
                    <div key={i} className="urun-row">
                      <div style={{ flex: 1 }}>
                        <div className="urun-text">{u.marka} — {u.urun}</div>
                        <input type="number" value={u.adet} min="1"
                          onChange={e => { if (!e.target.value) return; setForm(f => { const n = [...f.urunler]; n[i] = { ...n[i], adet: e.target.value }; return { ...f, urunler: n }; }); }}
                          style={{ width: 60, padding: '4px 8px', border: '1px solid #e0e4ff', borderRadius: 8, fontSize: 14, fontWeight: 600, textAlign: 'center', marginTop: 4 }}
                        />
                      </div>
                      <button type="button" className="btn-remove" onClick={() => removeUrun(i)}>✕</button>
                    </div>
                  ))}
                  <SearchDropdown label="Marka" value={form.marka} options={Object.keys(markalar).sort((a, b) => a.localeCompare(b, 'tr'))} onSelect={v => { setF('marka', v); setF('urun', ''); }} />
                  {form.marka && <SearchDropdown label="Ürün Adı" value={form.urun} options={markalar[form.marka] || []} onSelect={v => setF('urun', v)} />}
                  {form.urun && kasaGerektiriyor(form.marka, form.urun) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '10px 12px', background: '#fff8e1', borderRadius: 8, border: '1px solid #f59e0b' }}>
                      <span style={{ fontSize: 18 }}>⚠️</span>
                      <span style={{ fontSize: 14, color: '#b45309', fontWeight: 500 }}>Bu ürün için kasa siparişi vermeyi unutmayın!</span>
                    </div>
                  )}
                  <label className="label">Adet</label>
                  <input className="input" placeholder="Adet" type="number" value={form.adet} onChange={e => setF('adet', e.target.value)} />
                  <button type="button" className="btn-add-urun" onClick={() => {
                    if (!form.marka || !form.urun || !form.adet) { alert('Marka, ürün ve adet gerekli!'); return; }
                    setForm(f => ({ ...f, urunler: [...f.urunler, { marka: f.marka, urun: f.urun, adet: f.adet, teslimEdildi: false, teslimAdet: '' }], marka: '', urun: '', adet: '' }));
                  }}>+ Ürün Ekle</button>
                </div>
                <button className="btn-primary" type="submit" disabled={loading} style={{ width: '100%' }}>
                  {loading ? 'Kaydediliyor...' : editId ? 'Güncelle' : 'Siparişi Kaydet'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showCopyModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Eski Dönemden Kopyala</h2>
              <span className="dropdown-close" onClick={closeCopyModal}>✕</span>
            </div>
            <div className="modal-body">
              <label className="label">Hangi dönemden kopyalansın?</label>
              <select className="input" value={copyFromPeriodId} onChange={async e => {
                const id = e.target.value;
                setCopyFromPeriodId(id); setCopyFromOrders([]); setSelectedToCopy([]); setCopySearch(''); setCopyAdetler({});
                if (id) await loadCopyFromOrders(id);
              }}>
                <option value="">Dönem seçiniz...</option>
                {periods.filter(p => p.id !== selectedPeriod.id && p.status === 'closed').map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.startDate} → {p.endDate})</option>
                ))}
              </select>
              {copyFromOrders.length > 0 && (
                <div>
                  <input className="input" placeholder="Proje adı, bayi veya ürün ara..." value={copySearch} onChange={e => setCopySearch(e.target.value)} style={{ marginTop: 8 }} />
                  <p style={{ fontSize: 13, color: '#555', margin: '8px 0' }}>{filteredCopyOrders.length} sipariş — kopyalamak istediklerini seç:</p>
                  {filteredCopyOrders.map(o => {
                    const alreadyExists = existingOrderKeys.has(`${o.projeAdi}_${o.bayiAdi}_${o.projeDurumu}`);
                    return (
                      <div key={o.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid #f0f0f0', opacity: alreadyExists ? 0.5 : 1 }}>
                        <input type="checkbox" style={{ marginTop: 3 }} checked={alreadyExists || selectedToCopy.includes(o.id)} disabled={alreadyExists} onChange={() => !alreadyExists && toggleSelectCopy(o.id)} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{o.projeAdi}</div>
                            {alreadyExists && <span style={{ fontSize: 11, background: '#d1fae5', color: '#065f46', padding: '1px 8px', borderRadius: 10, fontWeight: 500 }}>Dönemde mevcut</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#888' }}>{o.bayiAdi} | {o.projeDurumu}</div>
                          {!alreadyExists && selectedToCopy.includes(o.id) && o.urunler?.map((u, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                              <span style={{ fontSize: 12, color: '#555', flex: 1 }}>• {u.marka} — {u.urun}</span>
                              <input type="number" value={copyAdetler[`${o.id}_${i}`] || u.adet} min="1"
                                onChange={e => setCopyAdetler(prev => ({ ...prev, [`${o.id}_${i}`]: e.target.value }))}
                                style={{ width: 60, padding: '3px 6px', border: '1px solid #e0e4ff', borderRadius: 6, fontSize: 13, fontWeight: 600, textAlign: 'center' }} />
                            </div>
                          ))}
                          {(alreadyExists || !selectedToCopy.includes(o.id)) && o.urunler?.map((u, i) => (
                            <div key={i} style={{ fontSize: 12, color: '#aaa' }}>• {u.marka} — {u.urun} x{u.adet}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <button className="btn-primary" onClick={copySelectedOrders} style={{ marginTop: 12, width: '100%' }}>
                    Seçilenleri Kopyala ({selectedToCopy.length})
                  </button>
                </div>
              )}
              {copyFromPeriodId && copyFromOrders.length === 0 && (
                <p style={{ color: '#888', textAlign: 'center', marginTop: 8 }}>Bu dönemde sipariş yok</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}